import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Package, Mail, Check, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Signup = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || "");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<'magic' | 'password'>("password");
  const [isSignup, setIsSignup] = useState(true);
  const [step, setStep] = useState<"email" | "sent" | "verified">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const claimSession = searchParams.get('claimSession');

  // Check if user is already authenticated
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // If they're already logged in and have a claimSession, convert it now
        if (claimSession) {
          await handleClaimBox(session.user.id);
        } else {
          // Otherwise redirect to dashboard
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profile && !profile.onboarding_completed) {
            navigate('/onboarding');
          } else {
            navigate('/dashboard');
          }
        }
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setStep("verified");
        
        // If there's a claimSession, convert the anonymous box
        if (claimSession) {
          await handleClaimBox(session.user.id);
        } else {
          toast({
            title: "Welcome to Endless Storage!",
            description: "You're now logged in.",
          });
          
          // Check profile completeness
          setTimeout(async () => {
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();

            if (profile && !profile.onboarding_completed) {
              navigate('/onboarding');
            } else {
              navigate('/dashboard');
            }
          }, 1000);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, toast, claimSession]);

  const handleClaimBox = async (userId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('convert-anonymous-box', {
        body: { 
          session_id: claimSession,
          email: email,
        }
      });

      if (error) {
        // Check if already converted
        if (error.message?.includes('already been converted')) {
          toast({
            title: "Already Saved!",
            description: "This box is already in your account",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Box Saved! ✨",
          description: "Your box has been added to your account",
        });
      }

      // Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error: any) {
      console.error('Claim box error:', error);
      toast({
        title: "Error saving box",
        description: error.message || "Please try again or contact support",
        variant: "destructive",
      });
      
      // Still navigate to dashboard after a delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2500);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError(null);

    try {
      const redirectUrl = `${window.location.origin}/signup${claimSession ? `?claimSession=${claimSession}` : ''}`;
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) throw error;

      setStep("sent");
      toast({
        title: "Magic link sent!",
        description: "Check your email for the login link.",
      });
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast({ title: "Welcome back!", description: "You're now logged in." });
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const redirectUrl = `${window.location.origin}/signup${claimSession ? `?claimSession=${claimSession}` : ''}`;
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectUrl },
      });
      if (signUpError) throw signUpError;

      // Send welcome email
      if (signUpData.user) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.functions.invoke('send-signup-welcome', {
            body: { email, name: email.split('@')[0] }
          });
        }
      }

      // Immediately sign in to avoid email link flow
      const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
      if (loginError) throw loginError;

      toast({ title: "Account created!", description: "You're now logged in." });
    } catch (err: any) {
      console.error('Sign up error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Package className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold text-primary">Endless Storage</span>
          </div>
          {claimSession ? (
            <p className="text-muted-foreground">Sign up to save your box</p>
          ) : (
            <p className="text-muted-foreground">Digital inventory made simple</p>
          )}
        </div>

        <Card className="shadow-elegant">
          <CardHeader>
            <CardTitle>
              {step === "email" && (claimSession ? "Save Your Box" : (isSignup ? "Create Your Account" : "Welcome Back"))}
              {step === "sent" && "Check Your Email"}
              {step === "verified" && "You're In!"}
            </CardTitle>
            <CardDescription>
              {step === "email" && (claimSession ? "Sign up to save your box to your account" : (isSignup ? "Get started with your free digital inventory" : "Sign in to access your inventory"))}
              {step === "sent" && "We've sent you a magic link"}
              {step === "verified" && (claimSession ? "Saving your box..." : "Redirecting to your dashboard...")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {step === "email" && (
              <>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Button type="button" variant={mode === 'password' ? 'default' : 'outline'} onClick={() => setMode('password')} size="sm">
                    Use password
                  </Button>
                  <Button type="button" variant={mode === 'magic' ? 'default' : 'outline'} onClick={() => setMode('magic')} size="sm">
                    Magic link
                  </Button>
                </div>

                {mode === 'password' ? (
                  <form onSubmit={isSignup ? handlePasswordSignUp : handlePasswordSignIn} className="space-y-4">
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      className="text-lg"
                    />
                    <Input
                      type="password"
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="text-lg"
                    />
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-primary" 
                      size="lg"
                      disabled={loading}
                    >
                      {loading ? (isSignup ? "Creating..." : "Signing in...") : (isSignup ? "Create account" : "Sign in")}
                    </Button>
                    <p className="text-sm text-muted-foreground text-center">
                      {isSignup ? (
                        <>
                          Already have an account?{" "}
                          <button type="button" className="underline" onClick={() => setIsSignup(false)}>Sign in</button>
                        </>
                      ) : (
                        <>
                          New here?{" "}
                          <button type="button" className="underline" onClick={() => setIsSignup(true)}>Create an account</button>
                        </>
                      )}
                    </p>
                  </form>
                ) : (
                  <form onSubmit={handleSendMagicLink} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="text-lg"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-primary" 
                      size="lg"
                      disabled={loading}
                    >
                      <Mail className="mr-2 h-5 w-5" />
                      {loading ? "Sending..." : "Send Magic Link"}
                    </Button>
                    <p className="text-sm text-muted-foreground text-center">
                      No password needed. We'll email you a secure login link.
                    </p>
                  </form>
                )}
              </>
            )}

            {step === "sent" && (
              <div className="text-center space-y-4">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  We sent a login link to <strong>{email}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  Click the link in your email to continue.
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => {
                    setStep("email");
                    setEmail("");
                  }}
                >
                  Use a different email
                </Button>
              </div>
            )}

            {step === "verified" && (
              <div className="text-center space-y-4">
                <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <Check className="h-8 w-8 text-white" />
                </div>
                <p className="font-semibold text-primary">
                  {claimSession ? "Box Saved!" : "Email Verified!"}
                </p>
                <p className="text-muted-foreground">
                  {claimSession ? "Adding to your account..." : "Taking you to your dashboard..."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Free digital inventory · $9.99/month optional storage</p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
